import React, { useState, useEffect, useCallback } from 'react';
import lpApi from '../../../helpers/lpApi';

// ============================================================================
// Big Projects · Contracts — admin list of Large Projects contracts with a
// status filter, totals, dates and a link to the contract PDF.
//
// Consumes besthand-api `listActiveContracts(status, limit, offset)` (returns
// `contracts`) through the `/lp-graphql` proxy via `lpApi`. The PDF link uses
// the contract's own `pdfUrl`. Admin-gated server-side (requireAdmin).
// ============================================================================

const Q_CONTRACTS = `
  query ($status: String, $limit: Int, $offset: Int) {
    listActiveContracts(status: $status, limit: $limit, offset: $offset) {
      status errorMessage
      contracts {
        id projectRequestId projectQuoteId clientUserId companyId
        status version totalAmount downPaymentAmount currency pdfUrl
        cancellationDeadline cancellationDaysApplied signedAtClient
        signedAtCompany cancelledAt completedAt createdAt
      }
    }
  }
`;

// ADMIN action — override cancel/void a contract beyond the cooling-off window.
// HELD escrow is refunded to the client; already-released funds stay with the
// company (a dispute decision, not a side effect). Admin-gated server-side.
const M_CANCEL_CONTRACT = `
  mutation ($contractId: String!, $reason: String) {
    adminCancelContract(contractId: $contractId, reason: $reason) {
      status errorMessage
      contract { id status cancelledAt }
    }
  }
`;

// Statuses we offer in the filter. '' = backend default (ACTIVE + cancellation).
const STATUSES = [
  { value: '', label: 'Default (active)' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'PENDING_SIGNATURE', label: 'Pending signature' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'IN_CANCELLATION_PERIOD', label: 'In cancellation period' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

const S = {
  page: { maxWidth: 1080, margin: '0 auto', padding: 24, fontFamily: "'Inter', -apple-system, sans-serif", color: '#0f1214' },
  h2: { margin: '0 0 4px', fontSize: 24, fontWeight: 800 },
  hint: { color: '#6b7280', fontSize: 14, marginBottom: 20 },
  toolbar: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18, flexWrap: 'wrap' },
  select: { padding: '9px 12px', border: '1px solid #d8dde2', borderRadius: 9, fontSize: 14, fontWeight: 600, outline: 'none', background: '#fff', cursor: 'pointer' },
  btnGhost: { background: '#fff', color: '#0f1214', border: '1px solid #e7e9ec', borderRadius: 9, padding: '8px 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  card: { border: '1px solid #e7e9ec', borderRadius: 14, padding: '16px 20px', marginBottom: 12, boxShadow: '0 6px 18px rgba(15,18,20,0.03)' },
  rowTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 },
  name: { fontSize: 17, fontWeight: 800, margin: 0 },
  amount: { fontSize: 20, fontWeight: 800, color: '#0f1214', whiteSpace: 'nowrap' },
  meta: { color: '#6b7280', fontSize: 13, marginTop: 4, lineHeight: 1.5 },
  link: { color: '#e8232b', fontWeight: 700, textDecoration: 'none', fontSize: 13 },
  badge: (kind) => {
    const map = {
      ACTIVE: { bg: '#e7f6ec', fg: '#137a37' },
      COMPLETED: { bg: '#e7f6ec', fg: '#137a37' },
      IN_CANCELLATION_PERIOD: { bg: '#fff3d6', fg: '#946200' },
      PENDING_SIGNATURE: { bg: '#eef2ff', fg: '#3730a3' },
      DRAFT: { bg: '#eef2ff', fg: '#3730a3' },
      CANCELLED: { bg: '#fde7e8', fg: '#c2151c' },
    };
    const c = map[kind] || { bg: '#eef2f4', fg: '#5a6168' };
    return { fontSize: 11, fontWeight: 800, letterSpacing: '0.05em', padding: '4px 10px', borderRadius: 999, background: c.bg, color: c.fg, whiteSpace: 'nowrap' };
  },
  empty: { color: '#6b7280', fontSize: 14, padding: '40px 0', textAlign: 'center' },
  alert: (ok) => ({ padding: '10px 14px', borderRadius: 8, fontSize: 14, marginBottom: 16, background: ok ? '#ecfdf5' : '#fef2f2', color: ok ? '#065f46' : '#991b1b' }),
  actions: { display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' },
  btnRed: { background: '#fff', color: '#c2151c', border: '1px solid #f3c9cb', borderRadius: 9, padding: '8px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
};

const fmtMoney = (v, cur) => (v == null ? '—' : `${cur && cur !== 'USD' ? `${cur} ` : '$'}${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const fmtDate = (s) => { if (!s) return ''; try { return new Date(s).toLocaleString(); } catch (e) { return s; } };

function Contracts() {
  const [contracts, setContracts] = useState([]);
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);
  const [busyId, setBusyId] = useState('');

  const firstError = (res) => (res && res.errors && res.errors.length ? res.errors[0].message : 'Something went wrong.');

  const load = useCallback(async (statusArg) => {
    setLoading(true);
    setMsg(null);
    try {
      const res = await lpApi(Q_CONTRACTS, { status: statusArg || null, limit: 200, offset: 0 });
      if (res.errors) {
        setMsg({ error: firstError(res) });
      } else {
        const env = res.data && res.data.listActiveContracts;
        if (env && env.status === 200) setContracts(env.contracts || []);
        else if (env) setMsg({ error: env.errorMessage || `Error ${env.status}` });
      }
    } catch (e) {
      setMsg({ error: `Could not load contracts: ${e.message}` });
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(status); }, [load, status]);

  const cancelContract = (c) => {
    const ok = typeof window !== 'undefined' && window.confirm(
      `CANCEL / VOID contract #${c.id} (${fmtMoney(c.totalAmount, c.currency)})?\n\n`
      + 'ESCROW IMPACT:\n'
      + '• Funds still HELD in escrow (captured but not yet released) are REFUNDED to the client (real Stripe refund).\n'
      + '• Funds already RELEASED to the company are LEFT WITH THE COMPANY — they are NOT clawed back here '
      + '(reversing a paid-out milestone is a dispute / refund decision).\n\n'
      + 'The contract becomes CANCELLED and its open milestones are cancelled. This cannot be undone.',
    );
    if (!ok) return;
    const reason = window.prompt(`Reason for cancelling contract #${c.id} (recommended):`);
    if (reason === null) return;
    setBusyId(`x${c.id}`);
    setMsg(null);
    lpApi(M_CANCEL_CONTRACT, { contractId: String(c.id), reason: reason.trim() || null })
      .then(async (res) => {
        if (res.errors) { setMsg({ error: firstError(res) }); setBusyId(''); return; }
        const env = res.data && res.data.adminCancelContract;
        if (env && env.status >= 200 && env.status < 300) { setMsg({ ok: `Contract #${c.id} cancelled. Held escrow refunded; released funds left with the company.` }); await load(status); }
        else { setMsg({ error: (env && env.errorMessage) || `Error ${env && env.status}` }); }
        setBusyId('');
      })
      .catch((e) => { setMsg({ error: `Action failed: ${e.message}` }); setBusyId(''); });
  };

  return (
    <div style={S.page}>
      <h2 style={S.h2}>Contracts</h2>
      <div style={S.hint}>
        Large Projects — signed contracts with their status, totals and dates. Filter by status and open the
        contract PDF for the full document.
      </div>

      <div style={S.toolbar}>
        <select style={S.select} value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Filter by status">
          {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <button style={S.btnGhost} onClick={() => load(status)} disabled={loading}>
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
        <span style={{ color: '#6b7280', fontSize: 13 }}>{contracts.length} shown</span>
      </div>

      {msg && <div style={S.alert(!!msg.ok)}>{msg.ok || msg.error}</div>}
      {loading && <div style={S.empty}>Loading…</div>}

      {!loading && (
        contracts.length === 0 ? (
          <div style={S.empty}>No contracts for this filter.</div>
        ) : contracts.map((c) => (
          <div style={S.card} key={c.id}>
            <div style={S.rowTop}>
              <div>
                <p style={S.name}>Contract #{c.id}{c.version ? ` · v${c.version}` : ''}</p>
                <div style={S.meta}>
                  Request #{c.projectRequestId}
                  {c.projectQuoteId ? ` · Quote #${c.projectQuoteId}` : ''}
                  {c.companyId ? ` · Company #${c.companyId}` : ''}
                  {c.clientUserId ? ` · Client ${c.clientUserId}` : ''}
                </div>
                <div style={S.meta}>
                  {c.downPaymentAmount != null ? `Down payment ${fmtMoney(c.downPaymentAmount, c.currency)} · ` : ''}
                  {c.signedAtClient ? `Client signed ${fmtDate(c.signedAtClient)} · ` : ''}
                  {c.signedAtCompany ? `Company signed ${fmtDate(c.signedAtCompany)} · ` : ''}
                  Created {c.createdAt ? fmtDate(c.createdAt) : '—'}
                </div>
                {c.cancellationDeadline ? (
                  <div style={S.meta}>
                    Cancellation deadline {fmtDate(c.cancellationDeadline)}
                    {c.cancellationDaysApplied ? ` (${c.cancellationDaysApplied}-day window)` : ''}
                  </div>
                ) : null}
                {c.cancelledAt ? <div style={S.meta}>Cancelled {fmtDate(c.cancelledAt)}</div> : null}
                {c.completedAt ? <div style={S.meta}>Completed {fmtDate(c.completedAt)}</div> : null}
                {c.pdfUrl ? (
                  <div style={S.meta}>
                    <a style={S.link} href={c.pdfUrl} target="_blank" rel="noreferrer">View contract PDF →</a>
                  </div>
                ) : null}
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={S.amount}>{fmtMoney(c.totalAmount, c.currency)}</div>
                <span style={S.badge(c.status)}>{(c.status || '—').replace(/_/g, ' ')}</span>
              </div>
            </div>
            {c.status !== 'CANCELLED' && c.status !== 'COMPLETED' && (
              <div style={S.actions}>
                <button style={S.btnRed} disabled={busyId === `x${c.id}`} onClick={() => cancelContract(c)}>
                  {busyId === `x${c.id}` ? '…' : 'Cancel / void contract'}
                </button>
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
}

export default Contracts;
